import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  getProfile,
  getMatch,
  updateMatchStatus,
  updateMatchLogistics,
  selectMenuItems,
  getShoppingList,
  fundMatch,
  uploadDocumentation,
  getDocumentation,
  createDonation,
  confirmDonation,
  createCheckoutSession,
  placeGroceryOrder,
  sendMessage,
  getMessages
} from '../../services/communityKitchenService';
import ConfirmModal from '../ConfirmModal';
import './CommunityKitchen.css';

const STATUS_PIPELINE = [
  { key: 'pending_recipient', label: 'Need Recipient', icon: '🍽️' },
  { key: 'pending_cook', label: 'Need Cook', icon: '⏳' },
  { key: 'pending_donor', label: 'Funding', icon: '💰' },
  { key: 'funded', label: 'Funded', icon: '✅' },
  { key: 'shopping', label: 'Shopping', icon: '🛒' },
  { key: 'cooking', label: 'Cooking', icon: '👨‍🍳' },
  { key: 'delivering', label: 'Delivery', icon: '🚗' },
  { key: 'completed', label: 'Done', icon: '🎉' }
];

const STATUS_TRANSITIONS = {
  pending_recipient: ['pending_cook', 'pending_donor', 'cancelled'],
  pending_cook: ['pending_donor', 'cancelled'],
  pending_donor: ['funded', 'cancelled'],
  funded: ['shopping', 'cancelled'],
  shopping: ['cooking', 'cancelled'],
  cooking: ['delivering', 'cancelled'],
  delivering: ['completed', 'cancelled'],
  completed: [],
  cancelled: []
};

const NEXT_ACTION_LABELS = {
  pending_cook: 'Assign Cook',
  pending_donor: 'Accept & Find Donor',
  funded: 'Mark as Funded',
  shopping: 'Start Shopping',
  cooking: 'Start Cooking',
  delivering: 'Out for Delivery',
  completed: 'Mark Delivered'
};

export default function CommunityKitchenMatchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef(null);

  const [match, setMatch] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [shoppingList, setShoppingList] = useState(null);
  const [documentation, setDocumentation] = useState([]);

  // Menu search state
  const [menuSearch, setMenuSearch] = useState('');
  const [menuResults, setMenuResults] = useState([]);
  const [menuSearching, setMenuSearching] = useState(false);
  const [pendingMenuItems, setPendingMenuItems] = useState([]);

  // Documentation upload
  const [docType, setDocType] = useState('photo');
  const [docCaption, setDocCaption] = useState('');
  const [docUrl, setDocUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  // Donation
  const [donationAmount, setDonationAmount] = useState(0);
  const [donating, setDonating] = useState(false);

  // Grocery ordering
  const [placingOrder, setPlacingOrder] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState('');

  // Messaging
  const [messages, setMessages] = useState([]);
  const [messageBody, setMessageBody] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);

  // Logistics confirmation
  const [logisticsMethod, setLogisticsMethod] = useState('');
  const [logisticsNotes, setLogisticsNotes] = useState('');
  const [savingLogistics, setSavingLogistics] = useState(false);

  // Store active match ID so menu browsing can add items
  useEffect(() => {
    if (id) sessionStorage.setItem('ck_active_match', id);
    return () => sessionStorage.removeItem('ck_active_match');
  }, [id]);

  const loadMatch = useCallback(async () => {
    try {
      const m = await getMatch(id);
      setMatch(m);
      if (m.amountFunded) setDonationAmount(m.amountFunded);
      if (m.selectedMenuItems?.length > 0) {
        setPendingMenuItems(m.selectedMenuItems.map(item => ({
          menuItemId: item.menuItemId?._id || item.menuItemId,
          title: item.menuItemId?.title || 'Menu Item',
          servings: item.servings || 1,
          imageUrl: item.menuItemId?.imageUrl
        })));
      }
    } catch (err) {
      toast.error('Failed to load match');
      navigate('/community-kitchen');
    }
  }, [id, navigate]);

  const loadProfile = useCallback(async () => {
    const p = await getProfile();
    setProfile(p);
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadMatch(), loadProfile()]);
      setLoading(false);

      // Load any saved menus from browsing
      const saved = JSON.parse(sessionStorage.getItem('ck_saved_menus') || '[]');
      if (saved.length > 0) {
        setPendingMenuItems(prev => {
          const existing = new Set(prev.map(p => p.menuItemId));
          const newItems = saved.filter(s => !existing.has(s._id)).map(s => ({
            menuItemId: s._id,
            title: s.title,
            servings: s.servings || 4,
            imageUrl: s.imageUrl
          }));
          if (newItems.length > 0) {
            toast.success(`${newItems.length} saved recipe${newItems.length > 1 ? 's' : ''} loaded from browsing`);
            setActiveSection('menus');
          }
          return [...prev, ...newItems];
        });
        sessionStorage.removeItem('ck_saved_menus');
      }
    };
    init();
  }, [loadMatch, loadProfile]);

  // Handle return from Stripe Checkout
  useEffect(() => {
    if (searchParams.get('funded') === 'true') {
      toast.success('Payment successful! The match has been funded.');
      setSearchParams({}, { replace: true });
      loadMatch();
    } else if (searchParams.get('funded') === 'false') {
      toast.error('Payment was cancelled.');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, loadMatch]);

  useEffect(() => {
    if (activeSection === 'shopping' && match?.selectedMenuItems?.length > 0) {
      getShoppingList(id).then(setShoppingList).catch(() => {});
    }
    if (activeSection === 'documentation') {
      getDocumentation(id).then(setDocumentation).catch(() => {});
    }
    if (activeSection === 'messages' && match?.contactRevealed) {
      getMessages(id).then(setMessages).catch(() => {});
    }
  }, [activeSection, id, match]);

  const myRole = profile && match ? (
    match.cookId?._id === profile._id ? 'cook' :
    match.recipientId?._id === profile._id ? 'recipient' :
    match.donorId?._id === profile._id ? 'donor' : null
  ) : null;

  // ─── Actions ───

  const handleStatusChange = async (newStatus) => {
    try {
      const updated = await updateMatchStatus(id, newStatus);
      setMatch(updated);
      toast.success(`Status updated to: ${NEXT_ACTION_LABELS[newStatus] || newStatus}`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const handleCancel = () => setCancelModalOpen(true);

  const confirmCancel = async () => {
    setCancelling(true);
    try {
      const updated = await updateMatchStatus(id, 'cancelled', 'Cancelled by participant');
      setMatch(updated);
      setCancelModalOpen(false);
      toast.success('Match cancelled');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCancelling(false);
    }
  };

  // ─── Menu Search ───

  const handleMenuSearch = async () => {
    if (!menuSearch.trim()) return;
    setMenuSearching(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/menu?search=${encodeURIComponent(menuSearch)}&limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Search failed');
      const data = await res.json();
      setMenuResults(Array.isArray(data) ? data : data.menuItems || data.items || []);
    } catch (err) {
      toast.error('Failed to search menus');
    } finally {
      setMenuSearching(false);
    }
  };

  const addMenuItem = (item) => {
    if (pendingMenuItems.find(p => p.menuItemId === item._id)) return;
    setPendingMenuItems(prev => [...prev, {
      menuItemId: item._id,
      title: item.title,
      servings: 1,
      imageUrl: item.imageUrl
    }]);
  };

  const removeMenuItem = (menuItemId) => {
    setPendingMenuItems(prev => prev.filter(p => p.menuItemId !== menuItemId));
  };

  const updateServings = (menuItemId, servings) => {
    setPendingMenuItems(prev => prev.map(p =>
      p.menuItemId === menuItemId ? { ...p, servings: Math.max(1, servings) } : p
    ));
  };

  const handleSaveMenus = async () => {
    try {
      const items = pendingMenuItems.map(p => ({
        menuItemId: p.menuItemId,
        servings: p.servings
      }));
      const updated = await selectMenuItems(id, items);
      setMatch(updated);
      toast.success('Menu selections saved!');
      setActiveSection('shopping');
    } catch (err) {
      toast.error(err.message);
    }
  };

  // ─── Documentation ───

  const handleDocUpload = async () => {
    setUploading(true);
    try {
      if (docType === 'external_link') {
        if (!docUrl.trim()) { toast.error('Please enter a URL'); setUploading(false); return; }
        await uploadDocumentation(id, { type: 'external_link', caption: docCaption, url: docUrl });
      } else {
        const file = fileInputRef.current?.files?.[0];
        if (!file) { toast.error('Please select a photo'); setUploading(false); return; }
        await uploadDocumentation(id, { type: 'photo', caption: docCaption, photo: file });
      }
      toast.success('Documentation uploaded!');
      setDocCaption('');
      setDocUrl('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      const docs = await getDocumentation(id);
      setDocumentation(docs);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  // ─── Donation ───

  const handleDonate = async () => {
    if (donationAmount < 100) { toast.error('Minimum donation is $1.00'); return; }
    setDonating(true);
    try {
      const result = await createCheckoutSession(id, donationAmount);

      if (result.devMode) {
        // Dev mode — auto-funded, no Stripe redirect needed
        toast.success('Match funded! Groceries can now be ordered.');
        await loadMatch();
      } else if (result.checkoutUrl) {
        // Production — redirect to Stripe Checkout
        window.location.href = result.checkoutUrl;
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDonating(false);
    }
  };

  // ─── Grocery Order ───

  const handlePlaceGroceryOrder = async () => {
    setPlacingOrder(true);
    try {
      const result = await placeGroceryOrder(id, deliveryAddress || undefined);
      if (result.devMode) {
        toast.success(`Dev Mode: Grocery order simulated with ${result.itemCount} items!`);
      } else if (result.instacartUrl) {
        window.open(result.instacartUrl, '_blank', 'noopener,noreferrer');
        toast.success(`Instacart order created with ${result.itemCount} items! Complete checkout in the new tab.`);
      } else {
        toast.success('Grocery order placed!');
      }
      await loadMatch();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPlacingOrder(false);
    }
  };

  // ─── Instacart ───

  const handleBuyOnInstacart = async () => {
    if (!shoppingList?.items?.length) {
      toast.error('No shopping list available');
      return;
    }
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/instacart/shopping-list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ingredients: shoppingList.items.map(i => ({
            name: i.name,
            quantity: i.quantity,
            unit: i.unit
          })),
          recipeTitle: `Community Kitchen Match`,
          menuItemUrl: `${window.location.origin}/community-kitchen/match/${id}`
        })
      });
      if (!res.ok) throw new Error('Failed to create Instacart list');
      const data = await res.json();
      window.open(data.url, '_blank', 'noopener,noreferrer');
      toast.success('Opening Instacart with your shopping list!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  // ─── Messages ───

  const handleSendMessage = async () => {
    if (!messageBody.trim()) return;
    setSendingMessage(true);
    try {
      const newMsg = await sendMessage(id, messageBody);
      setMessages(prev => [...prev, newMsg]);
      setMessageBody('');
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSendingMessage(false);
    }
  };

  // ─── Logistics ───

  const handleSaveLogistics = async () => {
    if (!logisticsMethod) {
      toast.error('Please select a delivery method');
      return;
    }
    setSavingLogistics(true);
    try {
      const updated = await updateMatchLogistics(id, {
        logisticsMethod,
        logisticsNotes: logisticsNotes.trim() || undefined
      });
      setMatch(updated);
      toast.success('Logistics confirmed!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingLogistics(false);
    }
  };

  // ─── Render ───

  if (loading) {
    return <div className="ck-dashboard"><div className="ck-loading">Loading match...</div></div>;
  }

  if (!match || !profile) return null;

  const isTerminal = ['completed', 'cancelled'].includes(match.status);
  const nextStatuses = STATUS_TRANSITIONS[match.status] || [];
  const nextAction = nextStatuses.find(s => s !== 'cancelled');
  const canAdvance = nextAction && (
    (myRole === 'cook' && ['pending_cook', 'funded', 'shopping', 'cooking', 'delivering'].includes(match.status)) ||
    (myRole === 'donor' && ['pending_recipient', 'pending_donor'].includes(match.status)) ||
    (myRole === 'recipient' && match.status === 'pending_recipient')
  );

  const recipient = match.recipientId || {};
  const cook = match.cookId || {};
  const donor = match.donorId || {};

  return (
    <div className="ck-match-detail">
      <button className="ck-btn ck-btn-secondary ck-btn-sm" onClick={() => navigate('/community-kitchen')} style={{ marginBottom: '1rem' }}>
        ← Back to Dashboard
      </button>

      {/* Status Pipeline */}
      <div className="ck-pipeline">
        {STATUS_PIPELINE
          .filter(step => {
            // Hide pending_recipient if match already has a recipient (started via cook/recipient flow)
            if (step.key === 'pending_recipient' && match.recipientId) return match.status === 'pending_recipient';
            // Hide pending_cook if match already has a cook (started via donor→cook flow)
            if (step.key === 'pending_cook' && match.cookId && !match.recipientId && match.status !== 'pending_cook') return false;
            return true;
          })
          .map((step, idx, filteredPipeline) => {
          const currentIdx = filteredPipeline.findIndex(s => s.key === match.status);
          const isCancelled = match.status === 'cancelled';
          const isActive = step.key === match.status;
          const isComplete = !isCancelled && idx < currentIdx;
          const isFuture = !isCancelled && idx > currentIdx;
          return (
            <div
              key={step.key}
              className={`ck-pipeline-step ${isActive ? 'active' : ''} ${isComplete ? 'complete' : ''} ${isFuture ? 'future' : ''} ${isCancelled ? 'cancelled' : ''}`}
            >
              <span className="ck-pipeline-icon">{isComplete ? '✓' : step.icon}</span>
              <span className="ck-pipeline-label">{step.label}</span>
            </div>
          );
        })}
        {match.status === 'cancelled' && (
          <div className="ck-pipeline-step active cancelled">
            <span className="ck-pipeline-icon">✕</span>
            <span className="ck-pipeline-label">Cancelled</span>
          </div>
        )}
      </div>

      {/* Action buttons */}
      {!isTerminal && (
        <div className="ck-match-actions">
          {canAdvance && (
            <button className="ck-btn ck-btn-primary" onClick={() => handleStatusChange(nextAction)}>
              {NEXT_ACTION_LABELS[nextAction] || `Move to ${nextAction}`}
            </button>
          )}
          {match.status === 'pending_donor' && profile.roles.includes('donor') && (
            <button className="ck-btn ck-btn-primary" onClick={() => setActiveSection('donate')}>
              💝 Fund This Match
            </button>
          )}
          <button className="ck-btn ck-btn-danger" onClick={handleCancel}>
            Cancel Match
          </button>
        </div>
      )}

      {/* Participants */}
      <div className="ck-participants">
        <div className={`ck-participant-card ${myRole === 'recipient' ? 'me' : ''}`}>
          <span className="ck-participant-role">🍽️ Recipient</span>
          <strong>{recipient.displayName || 'Pending'}</strong>
          {recipient.location && <span className="ck-location-text">{recipient.location.city}, {recipient.location.state}</span>}
          {match.contactRevealed && recipient.bio && <p className="ck-bio-preview">{recipient.bio}</p>}
        </div>
        <div className={`ck-participant-card ${myRole === 'cook' ? 'me' : ''}`}>
          <span className="ck-participant-role">👨‍🍳 Cook</span>
          <strong>{cook.displayName || 'Pending'}</strong>
          {cook.location && <span className="ck-location-text">{cook.location.city}, {cook.location.state}</span>}
          {match.contactRevealed && cook.cookDetails?.cuisineSpecialties?.length > 0 && (
            <span className="ck-bio-preview">Specialties: {cook.cookDetails.cuisineSpecialties.join(', ')}</span>
          )}
        </div>
        <div className={`ck-participant-card ${myRole === 'donor' ? 'me' : ''}`}>
          <span className="ck-participant-role">💝 Donor</span>
          <strong>{donor.displayName || 'Awaiting donor...'}</strong>
          {match.amountFunded > 0 && <span className="ck-donation-amount">${(match.amountFunded / 100).toFixed(2)} funded</span>}
        </div>
      </div>

      {match.contactRevealed && (
        <div className="ck-contact-revealed">
          <div className="ck-contact-header">✅ Contact information is now visible to all match participants</div>
          <div className="ck-contact-cards">
            {[
              { role: 'recipient', icon: '🍽️', data: recipient },
              { role: 'cook', icon: '👨‍🍳', data: cook },
              { role: 'donor', icon: '💝', data: donor }
            ].filter(p => p.data.displayName).map(p => (
              <div key={p.role} className="ck-contact-card">
                <div className="ck-contact-card-role">{p.icon} {p.data.displayName}</div>
                <div className="ck-contact-card-item">📧 {p.data.contactInfo?.email || <span className="ck-not-provided">No email provided</span>}</div>
                <div className="ck-contact-card-item">📱 {p.data.contactInfo?.phone || <span className="ck-not-provided">No phone provided</span>}</div>
                {p.role === 'recipient' && (
                  <div className="ck-contact-card-item">📍 {match.deliveryAddress || <span className="ck-not-provided">No address provided</span>}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Report Issue */}
      <div className="ck-report-issue">
        <Link
          to={`/support/new?category=community_kitchen&subject=${encodeURIComponent(`Issue with match #${id.slice(-6)}`)}`}
          className="ck-btn-link ck-report-link"
        >
          ⚠️ Report an issue with this match
        </Link>
      </div>

      {/* Section tabs */}
      <div className="ck-tabs">
        <button className={`ck-tab ${activeSection === 'overview' ? 'active' : ''}`} onClick={() => setActiveSection('overview')}>Overview</button>
        <button className={`ck-tab ${activeSection === 'menus' ? 'active' : ''}`} onClick={() => setActiveSection('menus')}>Menus</button>
        <button className={`ck-tab ${activeSection === 'shopping' ? 'active' : ''}`} onClick={() => setActiveSection('shopping')}>Shopping List</button>
        <button className={`ck-tab ${activeSection === 'documentation' ? 'active' : ''}`} onClick={() => setActiveSection('documentation')}>Documentation</button>
        {match.contactRevealed && <button className={`ck-tab ${activeSection === 'messages' ? 'active' : ''}`} onClick={() => setActiveSection('messages')}>Messages</button>}
        {match.status === 'pending_donor' && <button className={`ck-tab ${activeSection === 'donate' ? 'active' : ''}`} onClick={() => setActiveSection('donate')}>Donate</button>}
      </div>

      <div className="ck-section-content">
        {/* Overview */}
        {activeSection === 'overview' && (
          <div className="ck-overview">
            {/* Logistics confirmation — shown when not yet set and match has cook + recipient */}
            {!match.logisticsMethod && !isTerminal && match.cookId && match.recipientId && (
              <div className="ck-logistics-confirm">
                <h4>📦 How will the meal get to the recipient?</h4>
                <p className="ck-step-desc">Please confirm the delivery method so everyone knows the plan.</p>
                <div className="ck-logistics-options">
                  {[
                    { value: 'deliver', label: '🚗 Cook Delivers', desc: 'Cook brings the meal to the recipient' },
                    { value: 'pickup', label: '🏠 Recipient Picks Up', desc: 'Recipient picks up from the cook' },
                    { value: 'meetup', label: '🤝 Meet Up', desc: 'Meet at an agreed location' }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      className={`ck-logistics-option ${logisticsMethod === opt.value ? 'selected' : ''}`}
                      onClick={() => setLogisticsMethod(opt.value)}
                    >
                      <strong>{opt.label}</strong>
                      <span>{opt.desc}</span>
                    </button>
                  ))}
                </div>
                <div className="ck-field-group" style={{ marginTop: '0.75rem' }}>
                  <label className="ck-label">Notes (optional)</label>
                  <input
                    type="text"
                    className="ck-input"
                    placeholder="e.g. delivery time, address details, meeting spot..."
                    value={logisticsNotes}
                    onChange={e => setLogisticsNotes(e.target.value)}
                    maxLength={500}
                  />
                </div>
                <button
                  className="ck-btn ck-btn-primary"
                  onClick={handleSaveLogistics}
                  disabled={savingLogistics || !logisticsMethod}
                  style={{ marginTop: '0.75rem' }}
                >
                  {savingLogistics ? 'Saving...' : 'Confirm Logistics'}
                </button>
              </div>
            )}

            {/* Show confirmed logistics */}
            {match.logisticsMethod && (
              <div className="ck-logistics-confirmed">
                <strong>📦 Logistics:</strong>{' '}
                {match.logisticsMethod === 'deliver' ? '🚗 Cook Delivers' : match.logisticsMethod === 'pickup' ? '🏠 Recipient Picks Up' : '🤝 Meet Up'}
                {match.logisticsNotes && <span className="ck-logistics-notes"> — {match.logisticsNotes}</span>}
              </div>
            )}

            {/* Next step guidance */}
            {myRole && !isTerminal && (
              <div className="ck-next-step-hint" style={{ marginBottom: '1rem' }}>
                💡 {myRole === 'cook' && match.status === 'funded' && 'The match is funded! Go to the Menus tab to select recipes, then order groceries via Instacart.'}
                {myRole === 'cook' && match.status === 'pending_cook' && 'A recipient needs your help — accept this match to proceed.'}
                {myRole === 'cook' && match.status === 'pending_donor' && 'Waiting for a donor to sponsor the groceries. Share this match or sit tight!'}
                {myRole === 'cook' && match.status === 'shopping' && 'Complete your Instacart order if needed, then start cooking when ingredients arrive.'}
                {myRole === 'cook' && match.status === 'cooking' && 'Prepare the meal, then mark "Out for Delivery" when ready.'}
                {myRole === 'cook' && match.status === 'delivering' && 'Deliver the meal and click "Mark Delivered" when done.'}
                {myRole === 'recipient' && match.status === 'pending_recipient' && 'Review this match and accept to confirm you\'d like a meal.'}
                {myRole === 'recipient' && match.status === 'pending_cook' && 'Waiting for a cook to accept. Check back soon!'}
                {myRole === 'recipient' && match.status === 'pending_donor' && 'A cook is confirmed! Now waiting for a donor to fund the groceries.'}
                {myRole === 'recipient' && ['funded', 'shopping', 'cooking'].includes(match.status) && 'Your meal is in progress! The cook is handling everything.'}
                {myRole === 'recipient' && match.status === 'delivering' && 'Your meal is on the way!'}
                {myRole === 'donor' && match.status === 'pending_donor' && 'This match needs funding! Go to the Donate tab to sponsor the groceries.'}
                {myRole === 'donor' && match.status === 'funded' && 'You funded this meal! The cook will select recipes and order groceries next.'}
                {myRole === 'donor' && ['shopping', 'cooking', 'delivering'].includes(match.status) && 'The meal you sponsored is in progress.'}
              </div>
            )}
            <div className="ck-detail-grid">
              {match.scheduledDate && (
                <div className="ck-detail-item">
                  <label>Scheduled</label>
                  <span>{new Date(match.scheduledDate).toLocaleDateString()}</span>
                </div>
              )}
              {match.scheduledMeal && (
                <div className="ck-detail-item">
                  <label>Meal</label>
                  <span className="capitalize">{match.scheduledMeal}</span>
                </div>
              )}

              {match.fundingType && (
                <div className="ck-detail-item">
                  <label>Funding</label>
                  <span className="capitalize">{match.fundingType.replace('_', ' ')}</span>
                </div>
              )}
              {match.verificationRequired && (
                <div className="ck-detail-item">
                  <label>Verification</label>
                  <span className="capitalize">{match.verificationStatus?.replace('_', ' ')}</span>
                </div>
              )}
            </div>
            {match.logisticsNotes && (
              <div className="ck-notes-box">
                <strong>Logistics Notes:</strong> {match.logisticsNotes}
              </div>
            )}
            {match.cancelReason && (
              <div className="ck-notes-box ck-notes-cancel">
                <strong>Cancel Reason:</strong> {match.cancelReason}
              </div>
            )}
          </div>
        )}

        {/* Menu Selection */}
        {activeSection === 'menus' && (
          <div className="ck-menus-section">
            {['funded', 'shopping'].includes(match.status) && (myRole === 'cook' || myRole === 'donor') && (
              <div className="ck-next-step-hint" style={{ marginBottom: '1rem' }}>
                {match.instacartUrl
                  ? '🛒 Grocery order already placed! Go to the Shopping List tab to view the order status.'
                  : '🛒 Recipes are saved. Go to the Shopping List tab to order groceries via Instacart.'}
                <button
                  className="ck-btn ck-btn-primary ck-btn-sm"
                  style={{ marginLeft: '1rem' }}
                  onClick={() => setActiveSection('shopping')}
                >
                  Go to Shopping List →
                </button>
              </div>
            )}
            <h3>Select Recipes</h3>
            <p className="ck-step-desc">Choose recipes from FreshFitLab for this match.</p>

            <div className="ck-menu-search-bar">
              <input
                type="text"
                className="ck-input"
                placeholder="Search recipes..."
                value={menuSearch}
                onChange={e => setMenuSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleMenuSearch()}
              />
              <button className="ck-btn ck-btn-primary ck-btn-sm" onClick={handleMenuSearch} disabled={menuSearching}>
                {menuSearching ? 'Searching...' : 'Search'}
              </button>
            </div>

            <p className="ck-browse-hint">
              You can also <Link to="/menu">browse all menus</Link> and add recipes from there — just click "More" on any recipe and choose "Add to CK Match."
            </p>

            {menuResults.length > 0 && (
              <div className="ck-menu-results">
                {menuResults.map(item => (
                  <div key={item._id} className="ck-menu-result-item">
                    {item.imageUrl && <img src={item.imageUrl} alt={item.title} className="ck-menu-thumb" />}
                    <div className="ck-menu-result-info">
                      <strong>{item.title}</strong>
                      <span className="ck-location-text">{item.ingredients?.length || 0} ingredients · {item.servings || 4} servings</span>
                    </div>
                    <button
                      className="ck-btn ck-btn-primary ck-btn-sm"
                      onClick={() => addMenuItem(item)}
                      disabled={pendingMenuItems.find(p => p.menuItemId === item._id)}
                    >
                      {pendingMenuItems.find(p => p.menuItemId === item._id) ? 'Added' : '+ Add'}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {pendingMenuItems.length > 0 && (
              <div className="ck-selected-menus">
                <h4>Selected Recipes ({pendingMenuItems.length})</h4>
                {pendingMenuItems.map(item => (
                  <div key={item.menuItemId} className="ck-selected-menu-item">
                    {item.imageUrl && <img src={item.imageUrl} alt={item.title} className="ck-menu-thumb" />}
                    <div className="ck-menu-result-info">
                      <strong>{item.title}</strong>
                    </div>
                    <div className="ck-servings-control">
                      <button className="ck-btn ck-btn-secondary ck-btn-sm" onClick={() => updateServings(item.menuItemId, item.servings - 1)}>−</button>
                      <span className="ck-servings-count">{item.servings}</span>
                      <button className="ck-btn ck-btn-secondary ck-btn-sm" onClick={() => updateServings(item.menuItemId, item.servings + 1)}>+</button>
                      <span className="ck-servings-label">servings</span>
                    </div>
                    <button className="ck-btn ck-btn-danger ck-btn-sm" onClick={() => removeMenuItem(item.menuItemId)}>✕</button>
                  </div>
                ))}
                <button className="ck-btn ck-btn-primary" onClick={handleSaveMenus} style={{ marginTop: '1rem' }}>
                  Save Menu Selections
                </button>
              </div>
            )}
          </div>
        )}

        {/* Shopping List */}
        {activeSection === 'shopping' && (
          <div className="ck-shopping-section">
            <div className="ck-shopping-header">
              <h3>Shopping List</h3>
              {shoppingList?.items?.length > 0 && !['pending_cook', 'pending_donor'].includes(match.status) && (
                <button className="ck-btn ck-btn-primary ck-btn-sm" onClick={handleBuyOnInstacart}>
                  <img src="/images/Instacart_Carrot.png" alt="" style={{ width: 18, height: 18 }} />
                  Browse on Instacart
                </button>
              )}
            </div>

            {!shoppingList || shoppingList.items.length === 0 ? (
              <div className="ck-empty-state">
                <p>No menu items selected yet. Go to the Menus tab to choose recipes.</p>
              </div>
            ) : (
              <>
                <div className="ck-shopping-stats">
                  <span>{shoppingList.totalItems} items</span>
                </div>
                <div className="ck-shopping-list">
                  {shoppingList.items.map((item, idx) => (
                    <div key={idx} className={`ck-shopping-item ${item.isSpice ? 'spice' : ''} ${item.isOil ? 'oil' : ''}`}>
                      <span className="ck-shopping-qty">
                        {item.quantity} {item.unit}
                      </span>
                      <span className="ck-shopping-name">{item.name}</span>
                      {item.sources?.length > 0 && (
                        <span className="ck-shopping-sources">
                          from: {item.sources.join(', ')}
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                {/* Place Grocery Order — shown once match is funded */}
                {['funded', 'shopping'].includes(match.status) && (myRole === 'cook' || myRole === 'donor') && (
                  <div className="ck-grocery-order-section">
                    <div className="ck-grocery-order-card">
                      <h4>🛒 Order Groceries via Instacart</h4>
                      <p className="ck-step-desc">
                        {match.groceryOrderStatus === 'link_generated' || match.instacartUrl
                          ? 'Grocery order has been started! Click below to view or complete it on Instacart.'
                          : 'This match is funded. Place the grocery order through Instacart — the platform pays for ingredients from the donation.'}
                      </p>

                      {!match.instacartUrl && (
                        <div className="ck-field-group" style={{ marginBottom: '1rem' }}>
                          <label className="ck-label">Delivery Address (optional)</label>
                          <input
                            type="text"
                            className="ck-input"
                            placeholder="Where should groceries be delivered?"
                            value={deliveryAddress}
                            onChange={e => setDeliveryAddress(e.target.value)}
                          />
                        </div>
                      )}

                      {match.instacartUrl ? (
                        match.instacartUrl.includes('dev-simulated-order') ? (
                          <div className="ck-dev-badge">
                            ✅ Dev Mode: Grocery order simulated ({shoppingList?.totalItems || '?'} items)
                          </div>
                        ) : (
                          <a
                            href={match.instacartUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ck-btn ck-btn-primary"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}
                          >
                            <img src="/images/Instacart_Carrot.png" alt="" style={{ width: 20, height: 20 }} />
                            {match.groceryOrderStatus === 'link_generated' ? 'Complete Order on Instacart' : 'View Instacart Order'}
                          </a>
                        )
                      ) : (
                        <button
                          className="ck-btn ck-btn-primary"
                          onClick={handlePlaceGroceryOrder}
                          disabled={placingOrder}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                          <img src="/images/Instacart_Carrot.png" alt="" style={{ width: 20, height: 20 }} />
                          {placingOrder ? 'Creating Order...' : 'Place Grocery Order on Instacart'}
                        </button>
                      )}

                      {match.amountFunded > 0 && (
                        <p className="ck-hint" style={{ marginTop: '0.75rem' }}>
                          💰 ${(match.amountFunded / 100).toFixed(2)} available from donor funding
                        </p>
                      )}

                      {match.deliveryAddress && (
                        <p className="ck-hint">📍 Delivery: {match.deliveryAddress}</p>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Documentation */}
        {activeSection === 'documentation' && (
          <div className="ck-docs-section">
            <h3>Documentation</h3>
            <p className="ck-step-desc">Share photos, videos, or links to document this meal.</p>

            <div className="ck-doc-upload">
              <div className="ck-doc-type-toggle">
                <button
                  className={`ck-chip ${docType === 'photo' ? 'selected' : ''}`}
                  onClick={() => setDocType('photo')}
                >📷 Photo</button>
                <button
                  className={`ck-chip ${docType === 'external_link' ? 'selected' : ''}`}
                  onClick={() => setDocType('external_link')}
                >🔗 Link</button>
              </div>

              {docType === 'photo' ? (
                <input type="file" ref={fileInputRef} accept="image/*" className="ck-file-input" />
              ) : (
                <input
                  type="url"
                  className="ck-input"
                  placeholder="https://youtube.com/... or https://tiktok.com/..."
                  value={docUrl}
                  onChange={e => setDocUrl(e.target.value)}
                />
              )}

              <input
                type="text"
                className="ck-input"
                placeholder="Caption (optional)"
                value={docCaption}
                onChange={e => setDocCaption(e.target.value)}
                maxLength={300}
              />

              <button className="ck-btn ck-btn-primary ck-btn-sm" onClick={handleDocUpload} disabled={uploading}>
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>

            {documentation.length > 0 ? (
              <div className="ck-doc-gallery">
                {documentation.map((doc, idx) => (
                  <div key={doc._id || idx} className="ck-doc-item">
                    {doc.type === 'photo' || doc.type === 'video' ? (
                      <a href={doc.url} target="_blank" rel="noopener noreferrer">
                        <img src={doc.url} alt={doc.caption || 'Documentation'} className="ck-doc-thumb" />
                      </a>
                    ) : (
                      <a href={doc.url} target="_blank" rel="noopener noreferrer" className="ck-doc-link">
                        🔗 {doc.url.length > 50 ? doc.url.slice(0, 50) + '...' : doc.url}
                      </a>
                    )}
                    {doc.caption && <p className="ck-doc-caption">{doc.caption}</p>}
                    <span className="ck-doc-meta">
                      {doc.uploadedBy?.displayName || 'Participant'} · {new Date(doc.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="ck-empty-state" style={{ marginTop: '1rem' }}>
                <p>No documentation yet. Be the first to share!</p>
              </div>
            )}
          </div>
        )}

        {/* Messages */}
        {activeSection === 'messages' && match.contactRevealed && (
          <div className="ck-messages-section">
            <h3>Messages</h3>
            <p className="ck-step-desc">Chat with the other participants in this match.</p>

            <div className="ck-messages-container">
              {messages.length === 0 ? (
                <div className="ck-empty-state">
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                <div className="ck-messages-list">
                  {messages.map((msg) => {
                    const isMe = msg.senderId === profile._id || msg.senderId?._id === profile._id;
                    const roleIcon = msg.senderRole === 'recipient' ? '🍽️' : msg.senderRole === 'cook' ? '👨‍🍳' : '💝';
                    return (
                      <div key={msg._id} className={`ck-message-bubble ${isMe ? 'me' : 'other'}`}>
                        {!isMe && (
                          <div className="ck-message-sender">
                            {roleIcon} {msg.senderName}
                          </div>
                        )}
                        <div className="ck-message-body">{msg.body}</div>
                        <div className="ck-message-time">
                          {new Date(msg.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            <div className="ck-message-input-area">
              <input
                type="text"
                className="ck-input ck-message-input"
                placeholder="Type a message..."
                value={messageBody}
                onChange={e => setMessageBody(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                maxLength={2000}
                disabled={sendingMessage}
              />
              <button
                className="ck-btn ck-btn-primary ck-btn-sm"
                onClick={handleSendMessage}
                disabled={sendingMessage || !messageBody.trim()}
              >
                {sendingMessage ? '...' : 'Send'}
              </button>
            </div>
          </div>
        )}

        {/* Donation */}
        {activeSection === 'donate' && (
          <div className="ck-donate-section">
            <h3>Sponsor This Meal</h3>
            <p className="ck-step-desc">
              Your sponsorship covers the cost of groceries. FreshFitLab orders the ingredients 
              via Instacart and has them delivered to the cook — no money goes to individuals directly.
            </p>

            <div className="ck-donate-card">
              <div className="ck-how-it-works-mini">
                <div className="ck-how-step"><span className="ck-how-num">1</span> You fund the grocery cost</div>
                <div className="ck-how-step"><span className="ck-how-num">2</span> We order ingredients on Instacart</div>
                <div className="ck-how-step"><span className="ck-how-num">3</span> Cook receives groceries &amp; prepares the meal</div>
                <div className="ck-how-step"><span className="ck-how-num">4</span> Recipient enjoys a home-cooked meal</div>
              </div>

              <div className="ck-field-group" style={{ marginTop: '1.5rem' }}>
                <label className="ck-label">Sponsorship Amount</label>
                <div className="ck-amount-input">
                  <span className="ck-amount-prefix">$</span>
                  <input
                    type="number"
                    className="ck-input"
                    min={1}
                    step={0.01}
                    value={(donationAmount / 100).toFixed(2)}
                    onChange={e => setDonationAmount(Math.round(parseFloat(e.target.value || 0) * 100))}
                  />
                </div>
                {match.estimatedCost > 0 && donationAmount === 0 && (
                  <p className="ck-hint">Suggested amount covers grocery costs for the selected recipes.</p>
                )}
              </div>

              <button
                className="ck-btn ck-btn-primary"
                onClick={handleDonate}
                disabled={donating || donationAmount < 100}
                style={{ width: '100%' }}
              >
                {donating ? 'Processing...' : `Sponsor $${(donationAmount / 100).toFixed(2)} for Groceries`}
              </button>

              <p className="ck-hint" style={{ marginTop: '0.75rem', textAlign: 'center' }}>
                🔒 Secure payment via Stripe · Funds used exclusively for grocery purchases
              </p>
              <p className="ck-hint ck-commission-note" style={{ marginTop: '0.5rem', textAlign: 'center', fontSize: '12px' }}>
                FreshFitLab may receive a small affiliate commission from Instacart at no extra cost to you. 
                100% of your sponsorship goes toward groceries.
              </p>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={cancelModalOpen}
        title="Cancel Match"
        message="Are you sure you want to cancel this match? This action cannot be undone."
        confirmLabel="Cancel Match"
        cancelLabel="Keep Match"
        danger
        loading={cancelling}
        loadingLabel="Cancelling…"
        onConfirm={confirmCancel}
        onCancel={() => setCancelModalOpen(false)}
      />
    </div>
  );
}
